import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import Select from "react-select"
import DatePicker from "react-datepicker"
import moment from "moment"
import { DEFAULT_SEASON_YEAR, seasonYearsDescending } from "utils/seasonYear"
import UserService from "services/user.service"
import { DATE } from "pages/trainings/WeeklyStats"

const service = new UserService()

const GROUP_BY_OPTIONS = ["Manager", "Senior Manager", "Regional", "Senior Regional", "Partner", "Division"]

const GROUP_BY_TO_API: Record<string, string> = {
  Manager: "MGMT",
  "Senior Manager": "Sr MGMT",
  Regional: "Region",
  "Senior Regional": "Sr Region",
  Partner: "Partner",
  Division: "Division",
}

const MAX_POSITION_FILTER_OPTIONS = [
  "",
  "Rep",
  "Team Lead",
  "Manager",
  "Senior Manager",
  "Regional",
  "Senior Regional",
  "Partner",
]

type SignedRepsRow = {
  _id: string
  leader_name: string
  total_reps: number
  rookie_count?: number
  veteran_count?: number
  sophomore_count?: number
  fp_plus?: number
}

function BarCell({
  value,
  maxVal,
  colorClass = "bg-blue-500",
  formatValue,
}: {
  value: number
  maxVal: number
  colorClass?: string
  formatValue?: (v: number) => string
}) {
  const pct = maxVal > 0 ? (value / maxVal) * 100 : 0
  const display = formatValue ? formatValue(value) : value.toLocaleString()
  return (
    <div className="flex items-center gap-2 min-w-[100px]">
      <span className="text-sm tabular-nums shrink-0 text-ink w-12 text-right">{display}</span>
      <div className="flex-1 h-2 bg-surface-muted rounded-full overflow-hidden min-w-[60px]">
        <div className={`h-full rounded-full ${colorClass} opacity-80`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  )
}

export const SignedRepsReport = () => {
  const [groupBy, setGroupBy] = useState<string>("Manager")
  const [leaderIds, setLeaderIds] = useState<string[]>([])
  const [date, setDate] = useState<string>("YEARTODATE")
  const [onboardedDate, setOnboardedDate] = useState<string>("")
  const [showCustom, setShowCustom] = useState(false)
  const [showOnboardedCustom, setShowOnboardedCustom] = useState(false)
  const [startDate, setStartDate] = useState<Date | null>(null)
  const [endDate, setEndDate] = useState<Date | null>(null)
  const [onboardedStart, setOnboardedStart] = useState<Date | null>(null)
  const [onboardedEnd, setOnboardedEnd] = useState<Date | null>(null)
  const [year, setYear] = useState<string>(DEFAULT_SEASON_YEAR)
  const [recruiterId, setRecruiterId] = useState<string>("")
  const [recruiterName, setRecruiterName] = useState<string>("")
  const [maxPositionFilter, setMaxPositionFilter] = useState<string>("")
  const [divisionFilter, setDivisionFilter] = useState<string[]>([])
  const [divisionsFromServer, setDivisionsFromServer] = useState<string[]>([])
  const [leadersFromServer, setLeadersFromServer] = useState<{ id: string; name: string }[]>([])
  const [loading, setLoading] = useState(false)
  const [apiData, setApiData] = useState<Record<string, unknown> | null>(null)

  const groupByApi = groupBy ? (GROUP_BY_TO_API[groupBy] ?? "") : ""

  const leaderOptions = useMemo(
    () =>
      leadersFromServer.map((l) => ({
        value: l.id,
        label: `${l.name} (${l.id})`,
      })),
    [leadersFromServer]
  )
  const selectedLeaders = useMemo(
    () => leaderOptions.filter((o) => leaderIds.includes(o.value)),
    [leaderOptions, leaderIds]
  )

  const divisionOptions = useMemo(
    () => divisionsFromServer.map((d) => ({ value: d, label: d })),
    [divisionsFromServer]
  )
  const selectedDivisions = useMemo(
    () => divisionOptions.filter((o) => divisionFilter.includes(o.value)),
    [divisionOptions, divisionFilter]
  )

  const showLeaderDropdown = groupBy !== "Division"

  const queryParams = useMemo(
    () => ({
      groupBy: groupByApi || undefined,
      leaderId: showLeaderDropdown && leaderIds.length > 0 ? leaderIds : undefined,
      date: date || undefined,
      year: year || undefined,
      onboardedDate: onboardedDate || undefined,
      recruiterId: recruiterId.trim() || undefined,
      recruiterName: recruiterName.trim() || undefined,
      maxPosition: maxPositionFilter || undefined,
      division: divisionFilter.length > 0 ? divisionFilter : undefined,
    }),
    [
      groupByApi,
      showLeaderDropdown,
      leaderIds,
      date,
      year,
      onboardedDate,
      recruiterId,
      recruiterName,
      maxPositionFilter,
      divisionFilter,
    ]
  )

  const fetchReport = useCallback(() => {
    if (!groupByApi) return
    setLoading(true)
    service
      .getSignedRepsRegionalReporting(queryParams)
      .then((resp: any) => {
        if (resp?.data?.success && resp?.data?.data) setApiData(resp.data.data)
      })
      .catch(() => setApiData(null))
      .finally(() => setLoading(false))
  }, [queryParams, groupByApi])

  const initialFetchDone = useRef(false)
  useEffect(() => {
    if (!initialFetchDone.current && groupByApi) {
      initialFetchDone.current = true
      fetchReport()
    }
  }, [groupByApi, fetchReport])

  const fetchLeadersForDropdown = useCallback(() => {
    if (!groupBy || groupBy === "Division") return
    service
      .getSignedRepsLeaders(year, groupBy)
      .then((resp: any) => {
        if (resp?.data?.success && Array.isArray(resp.data.data)) {
          setLeadersFromServer(resp.data.data)
        } else {
          setLeadersFromServer([])
        }
      })
      .catch(() => setLeadersFromServer([]))
  }, [year, groupBy])

  const fetchDivisionsForDropdown = useCallback(() => {
    service
      .getSignedRepsDivisions(year)
      .then((resp: any) => {
        if (resp?.data?.success && Array.isArray(resp.data.data)) {
          setDivisionsFromServer(resp.data.data)
        } else {
          setDivisionsFromServer([])
        }
      })
      .catch(() => setDivisionsFromServer([]))
  }, [year])

  const { tableRows, dataGroupBy } = useMemo(() => {
    if (!apiData) return { tableRows: [] as SignedRepsRow[], dataGroupBy: null }
    const keyForCurrent = groupByApi ? (`by_${groupByApi}` as keyof typeof apiData) : null
    const arr = keyForCurrent ? (apiData[keyForCurrent] as SignedRepsRow[] | undefined) : undefined
    if (Array.isArray(arr) && arr.length > 0) {
      return { tableRows: arr, dataGroupBy: groupByApi }
    }
    const firstKey = Object.keys(apiData)[0] as keyof typeof apiData | undefined
    const fallback = firstKey && Array.isArray(apiData[firstKey]) ? (apiData[firstKey] as SignedRepsRow[]) : []
    const fallbackGroupBy = firstKey ? firstKey.replace(/^by_/, "") : null
    return { tableRows: fallback, dataGroupBy: fallbackGroupBy }
  }, [apiData, groupByApi])

  const maxReps = useMemo(() => (tableRows.length ? Math.max(...tableRows.map((r) => r.total_reps)) : 1), [tableRows])
  const maxRookie = useMemo(() => (tableRows.length ? Math.max(...tableRows.map((r) => r.rookie_count ?? 0)) : 1), [tableRows])
  const maxVeteran = useMemo(() => (tableRows.length ? Math.max(...tableRows.map((r) => r.veteran_count ?? 0)) : 1), [tableRows])
  const maxSophomore = useMemo(() => (tableRows.length ? Math.max(...tableRows.map((r) => r.sophomore_count ?? 0)) : 1), [tableRows])
  const maxFpPlus = useMemo(() => (tableRows.length ? Math.max(...tableRows.map((r) => r.fp_plus ?? 0), 1) : 1), [tableRows])

  const handleChangeDates = (dates: [Date | null, Date | null]) => {
    const [start, end] = dates
    setStartDate(start ?? null)
    setEndDate(end ?? null)
    if (start && end) {
      setDate(`${moment(start).format("YYYY-MM-DD")},${moment(end).format("YYYY-MM-DD")}`)
    }
  }

  const handleChangeOnboardedDates = (dates: [Date | null, Date | null]) => {
    const [start, end] = dates
    setOnboardedStart(start ?? null)
    setOnboardedEnd(end ?? null)
    if (start && end) {
      setOnboardedDate(`${moment(start).format("YYYY-MM-DD")},${moment(end).format("YYYY-MM-DD")}`)
    }
  }

  const totalReps = useMemo(() => tableRows.reduce((s, r) => s + (r.total_reps ?? 0), 0), [tableRows])
  const totalRookie = useMemo(() => tableRows.reduce((s, r) => s + (r.rookie_count ?? 0), 0), [tableRows])
  const totalVeteran = useMemo(() => tableRows.reduce((s, r) => s + (r.veteran_count ?? 0), 0), [tableRows])
  const totalSophomore = useMemo(() => tableRows.reduce((s, r) => s + (r.sophomore_count ?? 0), 0), [tableRows])
  const totalFpPlus = useMemo(() => tableRows.reduce((s, r) => s + (r.fp_plus ?? 0), 0), [tableRows])

  const colCount = 6

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="page-title">Signed Reps Report</h1>
        <p className="page-subtitle">Signed reps and performance metrics by hierarchy or division.</p>
      </div>

      <div className="card p-4">
        <div className="flex flex-wrap items-end gap-4">
          <div className="flex flex-col gap-1 min-w-[150px]">
            <label className="field-label">Group by</label>
            <select
              value={groupBy}
              onChange={(e) => {
                const v = e.target.value
                setGroupBy(v)
                setLeaderIds([])
                setLeadersFromServer([])
              }}
              className="px-3 py-2 text-sm"
            >
              {GROUP_BY_OPTIONS.map((o) => (
                <option key={o} value={o}>{o}</option>
              ))}
            </select>
          </div>

          {showLeaderDropdown && (
            <div className="flex flex-col gap-1 min-w-[260px]">
              <label className="field-label">Leader (optional)</label>
              <Select
                isMulti
                isSearchable
                placeholder="Select leaders..."
                value={selectedLeaders}
                options={leaderOptions}
                onChange={(selected) => setLeaderIds((selected || []).map((s) => s.value))}
                onMenuOpen={fetchLeadersForDropdown}
                className="react-select min-w-[240px]"
                classNamePrefix="react-select"
                styles={{
                  control: (base) => ({ ...base, backgroundColor: "#fff", borderColor: "#e6e8ec", boxShadow: "none" }),
                  menu: (base) => ({ ...base, backgroundColor: "#fff" }),
                  option: (base, state) => ({
                    ...base,
                    backgroundColor: state.isFocused ? "#f6f7f9" : "transparent",
                    color: "#111827",
                  }),
                  singleValue: (base) => ({ ...base, color: "#111827" }),
                  multiValue: (base) => ({ ...base, backgroundColor: "#f1f3f5" }),
                  multiValueLabel: (base) => ({ ...base, color: "#111827" }),
                  input: (base) => ({ ...base, color: "#111827" }),
                }}
              />
            </div>
          )}

          <div className="flex flex-col gap-1 min-w-[150px]">
            <label className="field-label">Year</label>
            <select
              value={year}
              onChange={(e) => setYear(e.target.value)}
              className="px-3 py-2 text-sm"
            >
              {seasonYearsDescending().map((y) => (
                <option key={y} value={String(y)}>{y}</option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-1 min-w-[150px]">
            <label className="field-label">Signed date</label>
            <select
              value={date}
              onChange={(e) => {
                if (e.target.value !== "CUSTOM") {
                  setDate(e.target.value)
                } else {
                  setShowCustom(true)
                }
              }}
              className="px-3 py-2 text-sm"
            >
              <option value="">Select</option>
              {DATE.map((d) => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>
            {showCustom && (
              <DatePicker
                selected={startDate}
                startDate={startDate}
                endDate={endDate}
                onChange={handleChangeDates}
                selectsRange
                isClearable
                placeholderText="Select range"
                className="z-[100] mt-2 py-2 w-60 rounded-lg border border-line bg-white shadow-card-lg text-ink px-3 text-sm"
                customInput={
                  <input
                    placeholder="Select date"
                    className="w-60 py-2 px-3 text-sm"
                  />
                }
              />
            )}
          </div>

          <div className="flex flex-col gap-1 min-w-[150px]">
            <label className="field-label">Onboarded date</label>
            <select
              value={onboardedDate || ""}
              onChange={(e) => {
                const v = e.target.value
                if (v !== "CUSTOM") {
                  setOnboardedDate(v)
                } else {
                  setShowOnboardedCustom(true)
                }
              }}
              className="px-3 py-2 text-sm"
            >
              <option value="">Any</option>
              {DATE.map((d) => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>
            {showOnboardedCustom && (
              <DatePicker
                selected={onboardedStart}
                startDate={onboardedStart}
                endDate={onboardedEnd}
                onChange={handleChangeOnboardedDates}
                selectsRange
                isClearable
                placeholderText="Select range"
                className="z-[100] mt-2 py-2 w-60 rounded-lg border border-line bg-white shadow-card-lg text-ink px-3 text-sm"
                customInput={
                  <input
                    placeholder="Select date"
                    className="w-60 py-2 px-3 text-sm"
                  />
                }
              />
            )}
          </div>

          <div className="flex flex-col gap-1 min-w-[150px]">
            <label className="field-label">Recruiter ID</label>
            <input
              type="text"
              value={recruiterId}
              onChange={(e) => setRecruiterId(e.target.value)}
              placeholder="Optional"
              className="rounded-lg border border-line bg-white text-ink px-3 py-2 text-sm focus:border-gray-500 focus:outline-none focus:ring-0"
            />
          </div>
          <div className="flex flex-col gap-1 min-w-[150px]">
            <label className="field-label">Recruiter name</label>
            <input
              type="text"
              value={recruiterName}
              onChange={(e) => setRecruiterName(e.target.value)}
              placeholder="Optional"
              className="rounded-lg border border-line bg-white text-ink px-3 py-2 text-sm focus:border-gray-500 focus:outline-none focus:ring-0"
            />
          </div>
          <div className="flex flex-col gap-1 min-w-[150px]">
            <label className="field-label">Max position</label>
            <select
              value={maxPositionFilter}
              onChange={(e) => setMaxPositionFilter(e.target.value)}
              className="px-3 py-2 text-sm"
            >
              {MAX_POSITION_FILTER_OPTIONS.map((o) => (
                <option key={o || "any"} value={o}>{o || "Any"}</option>
              ))}
            </select>
          </div>
          <div className="flex flex-col gap-1 min-w-[200px]">
            <label className="field-label">Division (filter)</label>
            <Select
              isMulti
              isSearchable
              placeholder="Any division..."
              value={selectedDivisions}
              options={divisionOptions}
              onChange={(selected) => setDivisionFilter((selected || []).map((s) => s.value))}
              onMenuOpen={fetchDivisionsForDropdown}
              className="react-select min-w-[180px]"
              classNamePrefix="react-select"
              styles={{
                control: (base) => ({ ...base, backgroundColor: "#fff", borderColor: "#e6e8ec", boxShadow: "none" }),
                menu: (base) => ({ ...base, backgroundColor: "#fff" }),
                option: (base, state) => ({
                  ...base,
                  backgroundColor: state.isFocused ? "#f6f7f9" : "transparent",
                  color: "#111827",
                }),
                singleValue: (base) => ({ ...base, color: "#111827" }),
                multiValue: (base) => ({ ...base, backgroundColor: "#f1f3f5" }),
                multiValueLabel: (base) => ({ ...base, color: "#111827" }),
                input: (base) => ({ ...base, color: "#111827" }),
              }}
            />
          </div>

          <div className="flex flex-col gap-1 min-w-[150px]">
            <label className="field-label invisible">Search</label>
            <button
              type="button"
              onClick={fetchReport}
              disabled={!groupByApi || loading}
              className="btn-primary"
            >
              {loading ? "Loading…" : "Search"}
            </button>
          </div>
        </div>
      </div>

      {dataGroupBy && groupByApi && dataGroupBy !== groupByApi && tableRows.length > 0 && (
        <div className="rounded-lg bg-amber-50 border border-amber-200 px-4 py-2 text-amber-800 text-sm">
          Showing data for <strong>{dataGroupBy}</strong>. Click Search to load data for <strong>{groupByApi}</strong>.
        </div>
      )}

      <div className="card overflow-hidden">
        <div className="px-4 py-3 border-b border-line">
          <h2 className="card-title">Signed Reps by {groupBy || "Group"}</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-line bg-surface text-ink-muted text-[11px] font-semibold uppercase tracking-wider">
                <th className="px-3 py-2.5 font-medium whitespace-nowrap text-ink">Name</th>
                <th className="px-3 py-2.5 font-medium whitespace-nowrap text-ink">Signed Reps</th>
                <th className="px-3 py-2.5 font-medium whitespace-nowrap text-ink">Rookie</th>
                <th className="px-3 py-2.5 font-medium whitespace-nowrap text-ink">Veteran</th>
                <th className="px-3 py-2.5 font-medium whitespace-nowrap text-ink">Sophomore</th>
                <th className="px-3 py-2.5 font-medium whitespace-nowrap text-ink">FP+</th>
              </tr>
            </thead>
            <tbody>
              {loading && !apiData ? (
                <tr>
                  <td colSpan={colCount} className="px-4 py-8 text-center text-ink-muted">Loading...</td>
                </tr>
              ) : !tableRows.length ? (
                <tr>
                  <td colSpan={colCount} className="px-4 py-8 text-center text-ink-muted">
                    {apiData ? "No data for the selected filters." : "Select group by and click Search."}
                  </td>
                </tr>
              ) : (
                tableRows.map((row, idx) => (
                  <tr key={row._id != null ? String(row._id) : `row-${idx}`} className="border-b border-line hover:bg-surface transition-colors">
                    <td className="px-2 py-3 text-ink text-sm whitespace-nowrap">{row.leader_name}</td>
                    <td className="px-2 py-3">
                      <BarCell value={row.total_reps} maxVal={maxReps} colorClass="bg-blue-500" />
                    </td>
                    <td className="px-2 py-3">
                      <BarCell value={row.rookie_count ?? 0} maxVal={maxRookie} colorClass="bg-emerald-500" />
                    </td>
                    <td className="px-2 py-3">
                      <BarCell value={row.veteran_count ?? 0} maxVal={maxVeteran} colorClass="bg-amber-500" />
                    </td>
                    <td className="px-2 py-3">
                      <BarCell value={row.sophomore_count ?? 0} maxVal={maxSophomore} colorClass="bg-violet-500" />
                    </td>
                    <td className="px-2 py-3">
                      <BarCell value={row.fp_plus ?? 0} maxVal={maxFpPlus} colorClass="bg-rose-500" formatValue={(v) => Number(v).toFixed(2)} />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
            <tfoot>
              <tr className="border-t border-line bg-surface text-ink font-semibold text-sm tabular-nums">
                <td className="px-2 py-3 whitespace-nowrap">Total</td>
                <td className="px-2 py-3">{totalReps.toLocaleString()}</td>
                <td className="px-2 py-3">{totalRookie.toLocaleString()}</td>
                <td className="px-2 py-3">{totalVeteran.toLocaleString()}</td>
                <td className="px-2 py-3">{totalSophomore.toLocaleString()}</td>
                <td className="px-2 py-3">{Number(totalFpPlus).toFixed(2)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  )
}
